/**
 * OBJKT API Client
 *
 * Frontend client for interacting with the OBJKT integration API.
 */

import { API_BASE_URL } from './config';
import type {
  ObjktListing,
  ObjktListingsResponse,
  ObjktOffersResponse,
  ObjktFloorOffersResponse,
  ObjktSyncStatus,
  ListingValidationResult,
  BatchValidationResponse,
} from './types/objkt';
import type {
  CheckoutIntentResponse,
  CheckoutConfirmResponse,
} from './types/cart';

const OBJKT_API_BASE = `${API_BASE_URL}/api/objkt`;

/**
 * Fetch active listings for a collection
 */
export async function fetchObjktListings(
  faContract: string,
  options: {
    sort?: 'price_asc' | 'price_desc' | 'recent';
    page?: number;
    pageSize?: number;
  } = {}
): Promise<ObjktListingsResponse> {
  const params = new URLSearchParams({
    sort: options.sort || 'price_asc',
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 50),
  });

  const response = await fetch(
    `${OBJKT_API_BASE}/collections/${faContract}/listings?${params}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch listings: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch all listings for a specific token
 */
export async function fetchObjktTokenListings(
  faContract: string,
  tokenId: string
): Promise<{ listings: ObjktListing[] }> {
  const response = await fetch(
    `${OBJKT_API_BASE}/tokens/${faContract}/${tokenId}/listings`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch token listings: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch offers for a specific token
 */
export async function fetchObjktTokenOffers(
  faContract: string,
  tokenId: string
): Promise<ObjktOffersResponse> {
  const response = await fetch(
    `${OBJKT_API_BASE}/tokens/${faContract}/${tokenId}/offers`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch token offers: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch floor offers for a collection
 */
export async function fetchObjktFloorOffers(
  faContract: string
): Promise<ObjktFloorOffersResponse> {
  const response = await fetch(
    `${OBJKT_API_BASE}/collections/${faContract}/floor-offers`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch floor offers: ${response.status}`);
  }

  return response.json();
}

/**
 * Validate a single listing (real-time check)
 */
export async function validateObjktListing(
  listingId: string
): Promise<ListingValidationResult> {
  const response = await fetch(`${OBJKT_API_BASE}/validate-listing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listingId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to validate listing: ${response.status}`);
  }

  return response.json();
}

/**
 * Validate multiple listings (batch)
 */
export async function validateObjktListings(
  listingIds: string[]
): Promise<BatchValidationResponse> {
  const response = await fetch(`${OBJKT_API_BASE}/validate-listings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listingIds }),
  });

  if (!response.ok) {
    throw new Error(`Failed to validate listings: ${response.status}`);
  }

  return response.json();
}

/**
 * Create a checkout intent (validates cart and returns totals)
 */
export async function createCheckoutIntent(
  buyerPkh: string,
  items: Array<{ listingId: string }>
): Promise<CheckoutIntentResponse> {
  const response = await fetch(`${OBJKT_API_BASE}/checkout/intents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ buyerPkh, items }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to create checkout intent: ${response.status}`);
  }

  return response.json();
}

/**
 * Confirm a checkout intent (re-validates before signing)
 */
export async function confirmCheckoutIntent(
  intentId: string,
  buyerPkh: string
): Promise<CheckoutConfirmResponse> {
  const response = await fetch(
    `${OBJKT_API_BASE}/checkout/intents/${intentId}/confirm`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buyerPkh }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to confirm intent: ${response.status}`);
  }

  return response.json();
}

/**
 * Get sync status for all collections
 */
export async function getObjktSyncStatus(): Promise<ObjktSyncStatus> {
  const response = await fetch(`${OBJKT_API_BASE}/sync/status`);

  if (!response.ok) {
    throw new Error(`Failed to get sync status: ${response.status}`);
  }

  return response.json();
}

/**
 * Force refresh a collection
 */
export async function refreshObjktCollection(
  faContract: string
): Promise<{ success: boolean; listings: number; floorOffers: number }> {
  const response = await fetch(
    `${OBJKT_API_BASE}/collections/${faContract}/refresh`,
    {
      method: 'POST',
    }
  );

  if (response.status === 429) {
    const error = await response.json();
    throw new Error(`Rate limited. Retry after ${error.retryAfter} seconds.`);
  }

  if (!response.ok) {
    throw new Error(`Failed to refresh collection: ${response.status}`);
  }

  return response.json();
}

/**
 * Convert XTZ to mutez (as string to avoid precision issues)
 */
export function xtzToMutez(xtz: string | number): string {
  const value = typeof xtz === 'string' ? parseFloat(xtz) : xtz;
  return Math.floor(value * 1_000_000).toString();
}

/**
 * Convert mutez to XTZ (formatted string)
 */
export function mutezToXtz(mutez: string | number, decimals: number = 2): string {
  const value = typeof mutez === 'string' ? BigInt(mutez) : BigInt(mutez);
  const xtz = Number(value) / 1_000_000;
  return xtz.toFixed(decimals);
}

/**
 * Format price for display
 */
export function formatPrice(xtz: string | number): string {
  const value = typeof xtz === 'string' ? parseFloat(xtz) : xtz;
  return `${value.toFixed(2)} XTZ`;
}
